import { z } from 'zod';

import { SITE_RUNTIME_STATE, type SiteRuntimeState } from '../fleet/inventory';

/**
 * Soll- und Ist-Zustand einer Site — zwei Vokabulare, niemals eines.
 *
 * Die Datenbank hält den Soll-Zustand (was der Betreiber will), der Heartbeat
 * den Ist-Zustand (was der Node vorfindet). Ein einziges Feld für beides würde
 * genau die Frage unbeantwortbar machen, um die es geht: "Läuft das, was laufen
 * soll?"
 *
 * ## Warum das Vokabular vom Ausgangskonzept abweicht
 *
 * `docs/architecture.md` nannte ursprünglich `pending` als Soll- und `failed`
 * als Ist-Zustand. Beides ist beim Entwurf des Reconcilers durchgefallen, und
 * die Begründung steht ausführlich in ADR 0005:
 *
 * - **`pending` ist eine Beobachtung, kein Wunsch.** Niemand *will*, dass eine
 *   Site "ausstehend" ist; das heißt nur "noch nicht abgeglichen". Im Soll wäre
 *   es genau das vermischte Feld, das die Trennung aufhebt.
 * - **`failed` ist das Ergebnis einer Operation, kein Befund über einen
 *   Container.** Stünde es im Ist, müsste der Reconciler bei `running/failed`
 *   entscheiden "nochmal versuchen oder nicht?" — eine Frage, die nur die
 *   Operationshistorie beantworten kann. Diese Vermischung ist der übliche
 *   Ursprung von Wiederholungsstürmen. Der Fehler gehört auf die Operation.
 * - **`suspended` ist `stopped` mit Begründung.** Zwei Soll-Zustände mit
 *   derselben Wirkung verdoppeln die Entscheidungstabelle ohne Erkenntnisgewinn.
 */

/** Was der Betreiber für eine Site will. Gepflegt ausschließlich vom Master. */
export const SITE_DESIRED_STATE = {
  /** Die Site soll laufen und erreichbar sein. */
  running: 'running',
  /** Die Site soll existieren, aber nicht laufen (Stilllegung, Zahlungsverzug). */
  stopped: 'stopped',
  /**
   * Die Site soll verschwinden.
   *
   * Bewusst ein Soll-Zustand und kein Lösch-Merker: Die Zeile darf erst
   * verschwinden, wenn der Ist-Zustand `missing` ist. Andernfalls verlöre man
   * den Auftrag aufzuräumen genau in dem Moment, in dem man ihn erteilt.
   */
  deleted: 'deleted',
} as const;

export type SiteDesiredState = (typeof SITE_DESIRED_STATE)[keyof typeof SITE_DESIRED_STATE];

export const siteDesiredStateSchema = z.enum([
  SITE_DESIRED_STATE.running,
  SITE_DESIRED_STATE.stopped,
  SITE_DESIRED_STATE.deleted,
]);

/**
 * Was der Master über die Site tatsächlich weiß. Abgeleitet aus dem Heartbeat,
 * niemals aus einer Zusage oder einem Rückruf.
 */
export const SITE_OBSERVED_STATE = {
  /**
   * Wir wissen es nicht — und handeln deshalb **nicht**.
   *
   * Der Node ist nicht online, sein Inventar ist veraltet, unvollständig oder
   * der Agent hat gerade neu gestartet. Die Unterscheidung zu `missing` ist die
   * wichtigste Zusicherung des ganzen Provisionierungsmodells: Wer sie aufgibt,
   * provisioniert nach dem ersten Netzwerkausfall eine ganze Maschine neu.
   */
  unknown: 'unknown',
  /** Der Node meldet nachweislich: Diese Site existiert hier nicht. */
  missing: 'missing',
  /** Der Stack wird gerade aufgebaut oder verändert. */
  transitioning: 'transitioning',
  /** Alle Container laufen und sind gesund. */
  running: 'running',
  /** Der Stack existiert, die Container sind gestoppt. */
  stopped: 'stopped',
  /** Der Stack existiert, mindestens ein Container ist ungesund oder gecrasht. */
  unhealthy: 'unhealthy',
} as const;

export type SiteObservedState = (typeof SITE_OBSERVED_STATE)[keyof typeof SITE_OBSERVED_STATE];

export const siteObservedStateSchema = z.enum([
  SITE_OBSERVED_STATE.unknown,
  SITE_OBSERVED_STATE.missing,
  SITE_OBSERVED_STATE.transitioning,
  SITE_OBSERVED_STATE.running,
  SITE_OBSERVED_STATE.stopped,
  SITE_OBSERVED_STATE.unhealthy,
]);

/**
 * Übersetzt einen Inventar-Eintrag des Workers in den Ist-Zustand des Masters.
 *
 * Nur für den Fall, dass ein Eintrag **vorhanden** ist. Ob ein fehlender
 * Eintrag `missing` oder `unknown` bedeutet, hängt davon ab, wie frisch und wie
 * vollständig der Snapshot ist — das weiß nur der Master und entscheidet seine
 * eigene reine Funktion `deriveObservedState`.
 */
export function toObservedState(runtimeState: SiteRuntimeState): SiteObservedState {
  switch (runtimeState) {
    case SITE_RUNTIME_STATE.running: {
      return SITE_OBSERVED_STATE.running;
    }
    case SITE_RUNTIME_STATE.stopped: {
      return SITE_OBSERVED_STATE.stopped;
    }
    case SITE_RUNTIME_STATE.transitioning: {
      return SITE_OBSERVED_STATE.transitioning;
    }
    case SITE_RUNTIME_STATE.unhealthy: {
      return SITE_OBSERVED_STATE.unhealthy;
    }
    case SITE_RUNTIME_STATE.unknown: {
      return SITE_OBSERVED_STATE.unknown;
    }
  }
}
